/**
 * Image resizing utility for handling image compression before upload
 */

/**
 * Resize an image to fit within max dimensions
 * @param {String} base64Data - Base64 encoded image data
 * @param {Number} maxWidth - Maximum width in pixels
 * @param {Number} maxHeight - Maximum height in pixels
 * @param {Number} quality - JPEG quality (0.0 - 1.0)
 * @returns {Promise<String|null>} Resized image as base64, or null if no resize needed
 */
export function resizeImage (base64Data, maxWidth = 1500, maxHeight = 1500, quality = 0.90) {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.src = base64Data

    img.onload = () => {
      let width = img.width
      let height = img.height

      // Skip resize if already small enough
      if (width <= maxWidth && height <= maxHeight) {
        resolve(null)
        return
      }

      // Calculate new dimensions while maintaining aspect ratio
      if (width > maxWidth) {
        height *= maxWidth / width
        width = maxWidth
      }

      if (height > maxHeight) {
        width *= maxHeight / height
        height = maxHeight
      }

      // Create canvas and resize
      const canvas = document.createElement('canvas')
      canvas.width = width
      canvas.height = height
      const ctx = canvas.getContext('2d')
      ctx.drawImage(img, 0, 0, width, height)

      // Convert to blob
      canvas.toBlob((resizedBlob) => {
        URL.revokeObjectURL(img.src)
        resolve(resizedBlob)
      }, 'image/jpeg', quality)
    }

    img.onerror = (error) => reject(error)
  })
}

/**
 * Convert a Blob to base64 string
 * @param {Blob} blob - Blob to convert
 * @returns {Promise<String>} Base64 encoded data URL
 */
export function blobToBase64 (blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => resolve(reader.result)
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })
}

/**
 * Check if a filename represents an image
 * @param {String} fileName - File name to check
 * @returns {Boolean} True if file is an image
 */
export function isImageFile (fileName) {
  if (!fileName) return false
  const ext = fileName.split('.').pop().toLowerCase()
  return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(ext)
}

/**
 * Process and resize an image file if needed
 * @param {String} base64Data - Base64 encoded image
 * @param {String} fileName - Original file name
 * @returns {Promise<String>} Processed (possibly resized) base64 data
 */
export async function processImage (base64Data, fileName) {
  // Only process images
  if (!isImageFile(fileName) || !base64Data.startsWith('data:image')) {
    return base64Data
  }

  try {
    const resizedBlob = await resizeImage(base64Data, 1500, 1500, 0.90)

    // If resize returned a blob, convert it to base64
    if (resizedBlob) {
      return await blobToBase64(resizedBlob)
    }

    // No resize needed, return original
    return base64Data
  } catch (error) {
    console.warn('Image resize failed, using original:', error)
    return base64Data
  }
}
